import { useEffect, useRef } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { toast } from "sonner";
import { useLocation } from "wouter";

// Tempo limite de inatividade: 40 minutos (em milissegundos)
const INACTIVITY_TIMEOUT_MS = 40 * 60 * 1000;
const STORAGE_KEY = "anota_last_activity_time";

export default function InactivityManager() {
  const { user, logout } = useAuth();
  const [, setLocation] = useLocation();
  const lastLoggedOutRef = useRef<boolean>(false);

  useEffect(() => {
    // Se o usuário não estiver autenticado, não ativa o monitor de inatividade
    if (!user) {
      lastLoggedOutRef.current = false;
      return;
    }

    // Inicializa o timestamp da última atividade na abertura
    localStorage.setItem(STORAGE_KEY, String(Date.now()));

    // Registra atividade com throttle para não degradar performance
    let lastRecorded = Date.now();
    const handleUserActivity = () => {
      const now = Date.now();
      if (now - lastRecorded > 2000) {
        lastRecorded = now;
        localStorage.setItem(STORAGE_KEY, String(now));
      }
    };

    // Eventos de interação do usuário na tela
    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "scroll",
      "wheel",
      "click",
      "pointerdown",
    ];

    events.forEach((evt) => {
      window.addEventListener(evt, handleUserActivity, { passive: true });
    });

    // Verificação periódica a cada 10 segundos
    const checkInterval = setInterval(() => {
      const storedTime = Number(localStorage.getItem(STORAGE_KEY) || Date.now());
      const elapsed = Date.now() - storedTime;

      if (elapsed >= INACTIVITY_TIMEOUT_MS && !lastLoggedOutRef.current) {
        lastLoggedOutRef.current = true;
        
        toast.warning(
          "Sua sessão foi encerrada por inatividade (40 minutos sem interação na tela).",
          {
            duration: 10000,
          }
        );

        logout()
          .then(() => {
            setLocation("/login");
          })
          .catch(() => {
            setLocation("/login");
          });
      }
    }, 10000);

    return () => {
      events.forEach((evt) => {
        window.removeEventListener(evt, handleUserActivity);
      });
      clearInterval(checkInterval);
    };
  }, [user, logout, setLocation]);

  return null;
}
